import { useMemo } from 'react';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import type { ServiceDeskQueuePage } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { doesExtensionMeetConditions } from '@atlassian/jira-forge-ui-utils/src/utils/conditions';
import { useDisplayConditionsConsistencyCheck } from '@atlassian/jira-forge-ui-utils/src/utils/display-conditions-transition/index.tsx';
import {
	isExtensionVisible,
	isExtensionHiddenByAppAccessRules,
} from '@atlassian/jira-forge-ui-utils/src/utils/extension/index.tsx';
import {
	forgeServiceDeskQueueModuleV1Resource,
	forgeServiceDeskQueueModuleV2Resource,
} from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { useResourceWithCustomRouterContext } from '@atlassian/jira-resource-with-custom-router-context/src/controllers/use-resource-with-custom-router-context/index.tsx';
import { useForgeItemStack } from '../../common/utils/find-item-stack/index.tsx';
import type { UseNavigationResourceReturnWithStackAndAN } from '../types';

export const useServiceDeskQueueForgeAppsV1 = (
	routeName?: string,
	parentStack?: string[],
	projectKey?: string,
): UseNavigationResourceReturnWithStackAndAN<ServiceDeskQueuePage[]> => {
	const { data, loading, error } = useResourceWithCustomRouterContext(
		forgeServiceDeskQueueModuleV1Resource,
		projectKey
			? {
					matchParams: { projectKey },
				}
			: undefined,
	);
	const serviceDeskQueueData = useMemo(() => data?.extensions || null, [data]);
	const accessNarrowedData = useMemo(() => data?.accessNarrowedExtensions || null, [data]);
	const stack = useForgeItemStack(serviceDeskQueueData, routeName, parentStack);

	return useMemo<UseNavigationResourceReturnWithStackAndAN<ServiceDeskQueuePage[]>>(
		() => ({
			data: serviceDeskQueueData
				? serviceDeskQueueData.filter((extension) => doesExtensionMeetConditions(extension, {}))
				: null,
			accessNarrowedData,
			loading,
			error,
			stack,
		}),
		[serviceDeskQueueData, accessNarrowedData, loading, error, stack],
	);
};

export const useServiceDeskQueueForgeAppsV2 = (
	routeName?: string,
	parentStack?: string[],
	projectKey?: string,
): UseNavigationResourceReturnWithStackAndAN<ServiceDeskQueuePage[]> => {
	const { data, loading, error } = useResourceWithCustomRouterContext(
		forgeServiceDeskQueueModuleV2Resource,
		projectKey
			? {
					matchParams: { projectKey },
				}
			: undefined,
	);

	// TODO: To be removed with new_graphql_endpoint_for_fetching_forge_modules FG cleanup
	useDisplayConditionsConsistencyCheck(data, {});

	const serviceDeskQueueData = useMemo(() => data?.filter(isExtensionVisible) || null, [data]);
	const accessNarrowedData = useMemo(
		() => data?.filter(isExtensionHiddenByAppAccessRules) || null,
		[data],
	);
	const stack = useForgeItemStack(serviceDeskQueueData, routeName, parentStack);

	return useMemo<UseNavigationResourceReturnWithStackAndAN<ServiceDeskQueuePage[]>>(
		() => ({
			data: serviceDeskQueueData,
			accessNarrowedData,
			loading,
			error,
			stack,
		}),
		[serviceDeskQueueData, accessNarrowedData, loading, error, stack],
	);
};

export const useServiceDeskQueueForgeApps = functionWithCondition(
	() => fg('new_graphql_endpoint_for_fetching_forge_modules'),
	useServiceDeskQueueForgeAppsV2,
	useServiceDeskQueueForgeAppsV1,
);

export const useServiceDeskQueueForgeAppsNav4 = (
	projectKey: string,
): UseNavigationResourceReturnWithStackAndAN<ServiceDeskQueuePage[]> =>
	useServiceDeskQueueForgeApps(undefined, undefined, projectKey);
